import * as React from "react";
import { Check } from "lucide-react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation } from "convex/react";
import { api } from "$convex/convexApi";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { formatCurrency, formatDate, formatStatusLabel } from "@/lib/format";
import { hasPermission } from "@/lib/roles";
import { getContractStatusBadgeClass } from "@/lib/status-tag-colors";
import { EditPrepaidInventoryDialog } from "../EditPrepaidInventoryDialog";

export default function SalesContractDetailPage() {
  const { id } = useParams();
  const { userRole } = useAuthIdentity();
  const contract = useQuery(api.contractWorkflow.getContractDetails, id ? { contractId: id as any } : "skip");

  const approveMutation = useMutation(api.contractWorkflow.adminApproveContract);
  const rejectMutation = useMutation(api.contractWorkflow.adminRejectContract);
  const submitMutation = useMutation(api.contractWorkflow.adminSubmitForApproval);
  const markSentMutation = useMutation(api.contractWorkflow.adminMarkAsSent);
  const markSignedMutation = useMutation(api.contractWorkflow.adminMarkAsSigned);
  const generatePdfMutation = useMutation(api.contractWorkflow.adminGeneratePDF);

  const [actionNotes, setActionNotes] = React.useState("");
  const [isActing, setIsActing] = React.useState(false);
  const canApproveContracts = hasPermission(userRole, "contracts:approve");

  const handleAction = React.useCallback(async (action: string) => {
    if (!id) return;
    if (action === "reject") {
      const rejectionReason = actionNotes.trim();
      if (!rejectionReason) {
        toast.error("Add a rejection reason before rejecting this contract");
        return;
      }
    }
    setIsActing(true);
    try {
      if (action === "submit") {
        await submitMutation({ contractId: id as any });
        toast.success("Contract submitted for approval");
      } else if (action === "approve") {
        await approveMutation({ contractId: id as any, notes: actionNotes || undefined });
        toast.success("Contract approved");
      } else if (action === "reject") {
        const rejectionReason = actionNotes.trim();
        await rejectMutation({ contractId: id as any, reason: rejectionReason, notes: rejectionReason });
        toast.success("Contract rejected");
      } else if (action === "markSent") {
        await markSentMutation({ contractId: id as any });
        toast.success("Contract marked as sent");
      } else if (action === "markSigned") {
        await markSignedMutation({ contractId: id as any });
        toast.success("Contract marked as signed");
      } else if (action === "generatePdf") {
        await generatePdfMutation({ contractId: id as any });
        toast.success("PDF generated");
      }
      setActionNotes("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Action failed");
    } finally {
      setIsActing(false);
    }
  }, [id, actionNotes, submitMutation, approveMutation, rejectMutation, markSentMutation, markSignedMutation, generatePdfMutation]);

  if (contract === undefined) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground">Loading contract...</CardContent>
      </Card>
    );
  }

  if (!contract) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Contract Not Found</CardTitle>
          <CardDescription>The requested contract could not be found.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild variant="outline"><Link to="/sales/contracts">Back to Contracts</Link></Button>
        </CardContent>
      </Card>
    );
  }

  const rawStatus = contract.status || contract.contractStatus;
  const status = rawStatus === "pending_approval" ? "pending" : rawStatus;
  const customerName =
    contract.customerName ??
    contract.user?.company ??
    contract.user?.name ??
    contract.user?.email ??
    "-";
  const totalValue = contract.totalValue ?? contract.totalAmount;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">{contract.contractNumber || `Contract`}</h1>
          <Badge variant="outline" className={getContractStatusBadgeClass(status)}>{formatStatusLabel(status)}</Badge>
        </div>
        <div className="flex items-center gap-2">
          {canApproveContracts && contract.isPrepaid && status === "active" ? (
            <EditPrepaidInventoryDialog
              contractId={id as string}
              items={(contract.items ?? []) as any[]}
              customerId={contract.userId as string | undefined}
              startDate={contract.startDate}
              endDate={contract.endDate}
              adminNotes={contract.adminNotes}
            />
          ) : null}
          <Button asChild variant="outline"><Link to="/sales/contracts">Back</Link></Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>Contract Details</CardTitle>
            <CardDescription>{customerName}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div className="grid gap-3 md:grid-cols-2">
              <DataPoint label="Customer" value={customerName} />
              <DataPoint label="Status" value={formatStatusLabel(status)} />
              <DataPoint label="Start Date" value={contract.startDate ? formatDate(contract.startDate) : "-"} />
              <DataPoint label="End Date" value={contract.endDate ? formatDate(contract.endDate) : "-"} />
              <DataPoint label="Volume (bags)" value={String(contract.volumeCommittedBags ?? 0)} />
              <DataPoint label="Total Value" value={typeof totalValue === "number" ? formatCurrency(totalValue) : "-"} />
              <DataPoint label="Contract Type" value={contract.contractType || "-"} />
              <DataPoint label="Payment Terms" value={contract.paymentTerms || "-"} />
            </div>
            {contract.items && contract.items.length > 0 ? (
              <div className="mt-4">
                <p className="mb-2 font-medium">Contract Items</p>
                <div className="space-y-2">
                  {contract.items.map((item: any, idx: number) => (
                    <div key={item.cargoOci || item.productName || idx} className="flex items-center justify-between rounded border p-2 text-sm">
                      <span>{item.cargoOci || item.productName || `Item ${idx + 1}`}</span>
                      <span>
                        {(item.totalBags ?? item.bags ?? item.quantity ?? 0)} bags @{" "}
                        {formatCurrency(item.unitPrice ?? item.pricePerBag ?? 0)}/lb
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            {Array.isArray(contract.releaseRows) && contract.releaseRows.length > 0 ? (
              <div className="mt-4">
                <p className="mb-2 font-medium">Release Schedule</p>
                <div className="space-y-2">
                  {contract.releaseRows.map((releaseRow: any) => (
                    <div key={releaseRow._id || releaseRow.releaseNumber} className="rounded border p-3 text-sm">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-medium">Release {releaseRow.releaseNumber}</span>
                        <Badge variant="outline" className={getContractStatusBadgeClass(releaseRow.status || "draft")}>{formatStatusLabel(releaseRow.status || "scheduled")}</Badge>
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground">
                        Start {releaseRow.clientSelectedStartDate ? formatDate(releaseRow.clientSelectedStartDate) : "-"}
                        {releaseRow.releaseDeadline ? ` · Deadline ${formatDate(releaseRow.releaseDeadline)}` : ""}
                        {releaseRow.gracePeriodEnds ? ` · Grace Ends ${formatDate(releaseRow.gracePeriodEnds)}` : ""}
                      </p>
                      {Array.isArray(releaseRow.allocations) && releaseRow.allocations.length > 0 ? (
                        <div className="mt-2 space-y-1">
                          {releaseRow.allocations.map((allocation: any) => (
                            <div key={allocation._id || `${releaseRow.releaseNumber}-${allocation.contractItemId}`} className="flex items-center justify-between text-xs">
                              <span>{allocation.productName || allocation.origin || "Contract Item"}</span>
                              <span>{allocation.fulfilledBags ?? 0}/{allocation.plannedBags ?? 0} bags</span>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            {contract.notes ? (
              <div className="mt-4">
                <p className="text-xs text-muted-foreground">Notes</p>
                <p>{contract.notes}</p>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <div className="space-y-6">
          {/* Status History */}
          {contract.statusHistory && contract.statusHistory.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Status History</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  <div className="relative pl-6">
                    <ol className="space-y-4 pb-1 text-sm">
                      {contract.statusHistory.map((entry: any, idx: number) => {
                        const isLast = idx === contract.statusHistory.length - 1;
                        const entryTimestamp = entry.timestamp || entry.changedAt;

                        return (
                          <li
                            key={`${entry.status}-${entry.timestamp}`}
                            className="relative flex items-start gap-3"
                          >
                            {!isLast ? (
                              <div className="absolute bottom-[-16px] left-[-15px] top-[22px] w-0.5 bg-emerald-500/25" />
                            ) : null}
                            <div className="absolute -left-6 z-10 mt-1 flex h-[18px] w-[18px] items-center justify-center rounded-full border-2 border-emerald-500 bg-emerald-500 text-white">
                              <Check className="h-2.5 w-2.5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-foreground">
                                {formatStatusLabel(entry.status)}
                              </p>
                              <p className="mt-0.5 text-xs text-muted-foreground">
                                {entryTimestamp ? formatDate(entryTimestamp) : ""}
                                {entry.notes ? ` — ${entry.notes}` : ""}
                              </p>
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : null}

          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Actions</CardTitle>
              <CardDescription>Available actions based on current status</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {canApproveContracts && (status === "pending" || status === "approved") ? (
                <div className="space-y-2">
                  <Label>Notes (optional)</Label>
                  <Textarea value={actionNotes} onChange={(e) => setActionNotes(e.target.value)} placeholder="Add notes for this action..." />
                </div>
              ) : null}

              <div className="flex flex-wrap gap-2">
                {canApproveContracts && status === "draft" ? (
                  <>
                    <Button asChild variant="outline" disabled={isActing}>
                      <Link to={`/sales/contracts/new?draftId=${id}`}>Edit Draft</Link>
                    </Button>
                    <Button onClick={() => void handleAction("submit")} disabled={isActing}>Submit for Approval</Button>
                  </>
                ) : null}
                {canApproveContracts && status === "pending" ? (
                  <>
                    <Button onClick={() => void handleAction("approve")} disabled={isActing}>Approve</Button>
                    <Button variant="destructive" onClick={() => void handleAction("reject")} disabled={isActing}>Reject</Button>
                  </>
                ) : null}
                {canApproveContracts && status === "approved" ? (
                  <>
                    <Button variant="outline" onClick={() => void handleAction("generatePdf")} disabled={isActing}>Generate PDF</Button>
                    <Button onClick={() => void handleAction("markSent")} disabled={isActing}>Mark as Sent</Button>
                  </>
                ) : null}
                {canApproveContracts && status === "sent" ? (
                  <Button onClick={() => void handleAction("markSigned")} disabled={isActing}>Mark as Signed</Button>
                ) : null}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function DataPoint({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border px-3 py-2">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-medium">{value}</p>
    </div>
  );
}
